import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Delete as DeleteIcon,
  Flag as FlagIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import eventsService, { Event } from '../../../services/events.service';
import { PageHeader, EmptyState } from '../../../components/Marketing';
import { fmtDateTime } from '../../../utils/date';

// ── Types ──────────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  uid: string;
  displayName: string;
  text: string;
  createdAt: string | null;
  deleted: boolean;
  flagged: boolean;
}

interface ChatSettings {
  ativo: boolean;
  apenas_attendees: boolean;
  moderacao: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

const EventChatPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState<string>('');

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [settings, setSettings] = useState<ChatSettings>({ ativo: true, apenas_attendees: true, moderacao: false });
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Load events list
  useEffect(() => {
    eventsService.getEvents({ limit: 100 })
      .then((res) => setEvents(res.data || []))
      .catch(() => {})
      .finally(() => setEventsLoading(false));
  }, []);

  // Load messages + settings when event changes
  const loadMessages = useCallback(async () => {
    if (!selectedEventId) return;
    setMessagesLoading(true);
    try {
      const [msgRes, setRes] = await Promise.all([
        api.get(`/admin/events/${selectedEventId}/chat/messages`),
        api.get(`/public/events/${selectedEventId}/chat/settings`),
      ]);
      setMessages(msgRes.data?.data || []);
      if (setRes.data?.data) {
        const s = setRes.data.data;
        setSettings({
          ativo: !!s.ativo,
          apenas_attendees: !!s.apenas_attendees,
          moderacao: !!s.moderacao,
        });
      }
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro ao carregar mensagens', severity: 'error' });
    } finally {
      setMessagesLoading(false);
    }
  }, [selectedEventId]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Delete message
  const handleDelete = async (messageId: string) => {
    if (!window.confirm('Eliminar esta mensagem?')) return;
    setDeletingId(messageId);
    try {
      await api.delete(`/admin/events/${selectedEventId}/chat/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      setToast({ msg: 'Mensagem eliminada', severity: 'success' });
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro ao eliminar', severity: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    if (!selectedEventId) return;
    setSettingsSaving(true);
    try {
      await api.post(`/admin/events/${selectedEventId}/chat/settings`, {
        ativo: settings.ativo ? 1 : 0,
        apenas_attendees: settings.apenas_attendees ? 1 : 0,
        moderacao: settings.moderacao ? 1 : 0,
      });
      setToast({ msg: 'Configurações guardadas', severity: 'success' });
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro ao guardar', severity: 'error' });
    } finally {
      setSettingsSaving(false);
    }
  };

  const selectedEvent = events.find((e) => String(e.id) === selectedEventId);

  const formatDate = (val: string | null) => {
    if (!val) return '—';
    // Instante → hora de CV, qualquer que seja o fuso do browser (o format() do date-fns usava o fuso local).
    return isNaN(new Date(val).getTime()) ? val : fmtDateTime(val);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Chat de Eventos"
        subtitle="Moderação e configuração do chat em tempo real"
        icon={<ChatIcon />}
        actions={
          selectedEventId ? (
            <Button startIcon={<RefreshIcon />} onClick={loadMessages} variant="outlined" size="small">
              Atualizar
            </Button>
          ) : null
        }
      />

      {/* Event selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl fullWidth size="small">
            <InputLabel>Seleciona um evento</InputLabel>
            <Select
              value={selectedEventId}
              label="Seleciona um evento"
              onChange={(e) => setSelectedEventId(e.target.value)}
              disabled={eventsLoading}
            >
              {eventsLoading ? (
                <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> A carregar...</MenuItem>
              ) : (
                events.map((ev) => (
                  <MenuItem key={ev.id} value={String(ev.id)}>
                    {ev.titulo} {ev.local_cidade ? `— ${ev.local_cidade}` : ''}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {!selectedEventId && (
        <EmptyState
          icon={<ChatIcon sx={{ fontSize: 48 }} />}
          title="Seleciona um evento"
          description="Escolhe um evento para ver e moderar as mensagens do chat."
        />
      )}

      {selectedEventId && (
        <Stack spacing={3}>
          {/* Settings card */}
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <SettingsIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>Configurações do Chat</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.ativo}
                      onChange={(e) => setSettings((s) => ({ ...s, ativo: e.target.checked }))}
                    />
                  }
                  label="Chat ativo"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.apenas_attendees}
                      onChange={(e) => setSettings((s) => ({ ...s, apenas_attendees: e.target.checked }))}
                    />
                  }
                  label="Apenas participantes com pass podem escrever"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.moderacao}
                      onChange={(e) => setSettings((s) => ({ ...s, moderacao: e.target.checked }))}
                    />
                  }
                  label="Modo de moderação (mensagens vistas mas não enviadas automaticamente)"
                />
              </Stack>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={settingsSaving}
                  startIcon={settingsSaving ? <CircularProgress size={16} /> : undefined}
                >
                  Guardar configurações
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Messages table */}
          <Card>
            <CardContent sx={{ pb: '8px !important' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <ChatIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Mensagens {!messagesLoading && `(${messages.length})`}
                </Typography>
              </Stack>
              <Divider sx={{ mb: 0 }} />
            </CardContent>

            {messagesLoading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : messages.length === 0 ? (
              <Box py={6}>
                <EmptyState
                  icon={<ChatIcon sx={{ fontSize: 40 }} />}
                  title="Sem mensagens"
                  description="Este evento ainda não tem mensagens no chat."
                />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Utilizador</TableCell>
                      <TableCell>Mensagem</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell align="center" width={80}>Flags</TableCell>
                      <TableCell align="center" width={60}>Ação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {messages.map((msg) => (
                      <TableRow
                        key={msg.id}
                        sx={{ opacity: msg.deleted ? 0.4 : 1, bgcolor: msg.flagged ? 'warning.lighter' : undefined }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{msg.displayName}</Typography>
                          <Typography variant="caption" color="text.secondary">{msg.uid?.slice(0, 8)}…</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word', maxWidth: 400 }}>
                            {msg.deleted ? <em style={{ color: '#999' }}>mensagem eliminada</em> : msg.text}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(msg.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {msg.flagged && (
                            <Tooltip title="Reportada pelos utilizadores">
                              <FlagIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {!msg.deleted && (
                            <Tooltip title="Eliminar mensagem">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(msg.id)}
                                  disabled={deletingId === msg.id}
                                >
                                  {deletingId === msg.id
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Stack>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toast?.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventChatPage;
